import { useEffect, useState } from 'react';
import { StepCard } from '../StepCard';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Input } from '../../../components/ui/input';
import axios from 'axios';
import { SelectGroup } from '../../../components/ui/selectDashboard';

// Options for "How did you hear about us?"
const HEAR_ABOUT_OPTIONS = [
  { value: 'MathonGo YouTube Channel', label: 'MathonGo YouTube Channel' },
  { value: 'Other YouTube Channels', label: 'Other YouTube Channels' },
  { value: 'Online Advertisement', label: 'Online Advertisement' },
  { value: 'Heard from Someone You Know', label: 'Heard from Someone You Know' },
  { value: 'Reddit/Quora', label: 'Reddit/Quora' },
  { value: 'other', label: 'Other' },
];

export function HearAboutUs({ onNext, onPrevious, data, onDataChange, onComplete }) {
  const [formData, setFormData] = useState({
    howDidYouHearAboutUs: data?.howDidYouHearAboutUs || '',
    customHearAboutUs: data?.customHearAboutUs || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    if (userProfile.howDidYouHearAboutUs) {
      const hearAboutValue = userProfile.howDidYouHearAboutUs;

      const isOther = !HEAR_ABOUT_OPTIONS.some((option) => option.value === hearAboutValue);
      const hearAboutUsValue = isOther ? 'other' : hearAboutValue || '';

      setFormData((prev) => ({
        ...prev,
        howDidYouHearAboutUs: hearAboutUsValue,
        customHearAboutUs: isOther ? hearAboutValue : '',
      }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.howDidYouHearAboutUs) {
      setErrors({ form: 'Please select how you heard about us' });
      return;
    }

    if (formData.howDidYouHearAboutUs === 'other' && !formData.customHearAboutUs) {
      setErrors({ form: 'Please specify how you heard about us' });
      return;
    }

    setIsLoading(true);
    try {
      const howDidYouHearAboutUs =
        formData.howDidYouHearAboutUs === 'other' ? formData.customHearAboutUs : formData.howDidYouHearAboutUs;

      const updateData = {
        howDidYouHearAboutUs: howDidYouHearAboutUs,
      };

      const response = await axios.patch(`/api/user/profile/`, updateData);

      if (response.status === 200) {
        localStorage.setItem('userProfile', JSON.stringify(response.data.data.user));
        localStorage.setItem('onboardingCompleted', 'true');
        onDataChange(response.data.data.user);
        onComplete();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'Failed to save data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors.form) {
      setErrors((prev) => ({ ...prev, form: '' }));
    }
  };

  const showCustomInput = formData.howDidYouHearAboutUs === 'other';

  return (
    <StepCard>
      <div className="flex flex-col h-full">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">
              How did you hear about us?
            </h2>
            <p className="text-[var(--q3-neutral-light)] text-sm sm:text-base">
              Help us understand how you discovered Quizrr!
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-4">
              <Select
                value={formData.howDidYouHearAboutUs}
                onValueChange={(value) => handleSelectChange('howDidYouHearAboutUs', value)}
              >
                <SelectTrigger className="border-[var(--q3-stroke-normal)] h-[40px] w-full rounded-full flex justify-between items-center px-4 focus:outline-none focus:ring-0">
                  {formData.howDidYouHearAboutUs ? (
                    <div className="flex items-center gap-1 w-full min-w-0">
                      <span className="shrink-0 text-[var(--q3-neutral-light)]">Source :</span>
                      <div className="flex-1 text-left truncate min-w-0 text-[var(--q3-neutral-default)]">
                        {(() => {
                          const selectedOption = HEAR_ABOUT_OPTIONS.find(
                            (option) => option.value === formData.howDidYouHearAboutUs,
                          );
                          const label = selectedOption?.label || '';
                          return label.length > 35 ? label.slice(0, 35) + '...' : label;
                        })()}
                      </div>
                    </div>
                  ) : (
                    <SelectValue placeholder="Where did you hear about MathonGo?" />
                  )}
                </SelectTrigger>
                <SelectContent className="rounded-xl bg-[var(--q3-surface-default)] shadow-lg w-(--radix-select-trigger-width) min-w-(--radix-select-trigger-width) pr-6">
                  <SelectGroup>
                    {HEAR_ABOUT_OPTIONS.map((option) => (
                      <SelectItem
                        className="cursor-pointer rounded-3xl hover:bg-[var(--q3-surface-dimmer)]"
                        key={option.value}
                        value={option.value}
                      >
                        <div className="w-full">
                          <span className="text-sm leading-relaxed break-words">{option.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              {showCustomInput && (
                <div className="relative h-[40px] w-full rounded-full border border-input bg-background px-4 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 flex items-center">
                  {formData.customHearAboutUs && (
                    <span className="shrink-0 mr-1 text-[var(--q3-neutral-light)]">Please specify :</span>
                  )}
                  <Input
                    type="text"
                    placeholder={formData.customHearAboutUs ? '' : 'Please specify how you heard about us'}
                    value={formData.customHearAboutUs}
                    onChange={(e) => handleSelectChange('customHearAboutUs', e.target.value)}
                    className="flex-1 h-full w-full bg-transparent border-none p-0 text-[var(--q3-neutral-default)] focus:outline-none focus:ring-0"
                    required={formData.howDidYouHearAboutUs === 'other'}
                  />
                </div>
              )}
            </div>

            {errors.form && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.form}</div>}
            {errors.submit && <div className="text-[var(--q3-danger-normal)] text-sm text-center">{errors.submit}</div>}
          </form>
        </div>

        <div className="flex flex-col sm:flex-row pt-4 gap-4 sm:gap-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onPrevious}
            className="w-full sm:flex-shrink-0 sm:flex-grow-0 sm:basis-1/3 px-4 sm:px-8 rounded-full bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-default)]/90 text-[var(--q3-neutral-default)] disabled:opacity-50 border border-[var(--q3-stroke-normal)]"
            disabled={isLoading}
          >
            Previous
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            className="w-full sm:flex-shrink-0 sm:flex-grow sm:basis-2/3 bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] disabled:opacity-50 px-4 sm:px-8 rounded-full"
            disabled={isLoading}
          >
            {isLoading ? 'Completing...' : 'Complete Setup'}
          </Button>
        </div>
      </div>
    </StepCard>
  );
}
